import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import { CircularProgress, FormControlLabel, Stack, Switch, Typography } from '@mui/material';

import BedbankService from '~/services/BedbankService';

import NestedAccordion from './NestedAccordion';

type KillswitchKey = 'travelgateSpecialRequestsEnabled' | 'expediaOutsidePolicyCancellationEnabled';

const KILLSWITCHES: Array<{
  key: KillswitchKey;
  label: string;
  description: string;
  on: string;
  off: string;
  update: (enabled: boolean) => Promise<unknown>;
}> = [
  {
    key: 'travelgateSpecialRequestsEnabled',
    label: 'Travelgate special requests',
    description:
      'Turns post-booking special requests on or off for every Travelgate bedbank. Other processes pick this up within two minutes.',
    on: 'Travelgate special requests are on',
    off: 'Travelgate special requests are off',
    update: (enabled) => BedbankService.updateTravelgateSpecialRequests(enabled),
  },
  {
    key: 'expediaOutsidePolicyCancellationEnabled',
    label: 'Expedia outside-policy cancellation',
    description:
      'Turns outside-policy cancellation requests on or off for Expedia. Other processes pick this up within two minutes.',
    on: 'Expedia outside-policy cancellation is on',
    off: 'Expedia outside-policy cancellation is off',
    update: (enabled) => BedbankService.updateExpediaOutsidePolicyCancellation(enabled),
  },
];

export default function DynamicConfigurationsPage() {
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [saving, setSaving] = useState<KillswitchKey | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    async function fetchConfiguration() {
      try {
        const data = await BedbankService.getDynamicConfigurations();
        setData(data.result);
      } catch (e) {
        console.error(e);
        enqueueSnackbar('Error loading dynamic configurations', { variant: 'error' });
      }
    }

    fetchConfiguration();
  }, [enqueueSnackbar]);

  async function handleKillswitch(key: KillswitchKey, enabled: boolean) {
    const killswitch = KILLSWITCHES.find((candidate) => candidate.key === key);
    if (!killswitch) {
      return;
    }

    setSaving(key);
    try {
      await killswitch.update(enabled);
      const refreshed = await BedbankService.getDynamicConfigurations();
      setData(refreshed.result);
      enqueueSnackbar(enabled ? killswitch.on : killswitch.off, { variant: 'success' });
    } catch (e) {
      console.error(e);
      enqueueSnackbar(`Could not update ${killswitch.label}`, { variant: 'error' });
    } finally {
      setSaving(null);
    }
  }

  if (data === null) {
    return <CircularProgress />;
  }

  return (
    <>
      <Stack sx={{ px: 2, py: 1 }} spacing={2}>
        {KILLSWITCHES.map((killswitch) => (
          <div key={killswitch.key}>
            <FormControlLabel
              control={
                <Switch
                  checked={data[killswitch.key] === true}
                  disabled={saving !== null}
                  onChange={(_event, checked) => {
                    handleKillswitch(killswitch.key, checked);
                  }}
                />
              }
              label={killswitch.label}
            />
            <Typography variant="body2" color="text.secondary">
              {killswitch.description}
            </Typography>
          </div>
        ))}
      </Stack>
      {Object.entries(data).map(([key, value]) => (
        <NestedAccordion key={key} keyName={key} value={value} />
      ))}
    </>
  );
}
